import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { PageEvent } from 'src/app/shared/directives/paginator.component';
import { Trade } from '../models/trade';
import { TradeSummary } from '../models/trade-summary';
import _ from 'lodash';
import { TableColumn, TableColumnType } from '../models/allocation-table';
import { faFilterCircleXmark, faPlus, faMinus, faScaleBalanced } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'app-allocations-table',
    templateUrl: './allocations-table.component.html',
    styleUrl: './allocations-table.component.scss',
    standalone: false
})
export class AllocationsTableComponent implements OnInit, OnChanges {
  faFilterCircleXmark: any = faFilterCircleXmark;
  faPlus: any = faPlus;
  faMinus: any = faMinus;
  faScaleBalanced: any = faScaleBalanced;

  @Input() columns: TableColumn[];
  @Input() data: Trade[];
  @Input() showTradeOrders: boolean = false;
  @Output() onAllocateNextClick = new EventEmitter<Trade>;
  @Output() onRefresh = new EventEmitter;

  /** All data after removing fully-allocated accounts */
  allData: Trade[] = [];
  /** Data after applying column filters */
  filteredData: Trade[] = [];
  /** Current page slice for the template */
  pagedData: Trade[] = [];

  totalRecords: number = 0;
  filteredTotalRecords: number = 0;
  pageIndex: number = 0;
  pageSize: number = 10;

  TableColumnType = TableColumnType;

  @ViewChildren('filter', { read: ElementRef }) filters: QueryList<ElementRef>;

  constructor(){
  }

  ngOnInit(){
  }

  ngOnChanges(changes: SimpleChanges): void {
      if(this.data){
        this.totalRecords = this.data.length;
        this.setPagedData();
      }
  }

  getFilterValue(column: TableColumn): string {
    const filterEl = this.filters?.find(input => input.nativeElement.id === `${column.key}-filter`);
    let value: string = filterEl ? filterEl.nativeElement.value : '';
    if(value){
      switch(column.type){
        case TableColumnType.currency:
          let valueParts = value.split('.');
          if(valueParts.length === 2 && valueParts[1] === ''){
            value = value.substring(0, value.length - 1);
          }
          return this.formatNumberCurrency(value).replace('.00','');
        case TableColumnType.number:
          return this.formatNumberCurrency(value).replace('$','');
        default:
          return value;
      }
    }
    return value;
  }

  clearFilters(): void {
    this.filters.forEach(input => input.nativeElement.value = '');
    this.applyFilters();
  }

  public pageChangeEvent(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.getPagedData();
  }

  // Apply filter on column
  public applyFilters(): void {
    this.filteredData = this.allData.filter(data => {
      return _.every(this.columns, (column: TableColumn) => {
        if (column.isDisplayed && column.isFilterEnabled) {
          let filterValue = this.getFilterValue(column).trim().toLowerCase();
          if (!filterValue) return true;
          switch (column.type) {
            case TableColumnType.currency:
              return this.getColumnValue(column, data[column.key])?.toString().trim().toLowerCase().includes(filterValue);
            case TableColumnType.number:
              return this.getColumnValue(column, data[column.key])?.toString().trim().toLowerCase() === filterValue;
            default:
              return this.getColumnValue(column, data[column.key])?.toString().trim().toLowerCase().includes(filterValue);
          }
        }
        return true;
      });
    });
    this.filteredTotalRecords = this.filteredData.length;
    this.pageIndex = 0;
    this.getPagedData();
  }

  getColumnValue(column: TableColumn, value) {
    if(value){
      switch(column.type){
        case TableColumnType.currency:
          return this.formatNumberCurrency(value).replace('.00','');
        case TableColumnType.number:
          return this.formatNumberCurrency(value).replace('$','');
        default:
          return value;
      }
    }
    return value;
  }

  formatNumberCurrency(number) : string {
    const formattedValue = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number).replace(',','').replace('.00', '');

    return formattedValue;
  }

  public setPagedData() {
    // Filter out fully allocated accounts
    this.allData = this.data.filter(item => {
      return !this.isAccountFullAllocated(item.orders);
    });

    this.totalRecords = this.allData.length;
    this.filteredData = [...this.allData];
    this.filteredTotalRecords = this.filteredData.length;
    this.pageIndex = 0;
    this.getPagedData();
  }

  private getPagedData(): void {
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    this.pagedData = this.filteredData.slice(start, end);
  }

  public getDisplayedColumnKeys(): string[]{
    return this.columns.filter(column => column.isDisplayed).map(column => column.key);
  }

  public checkIfAnyOrderChecked(element: Trade) {
    return element.ordersChecked > 0;
  }

  public checkIfAnyQuantityToAllocate(element: Trade){
    let result = _.sumBy(element.orders, order => order.quantityToAllocate ) > 0;
    return result;
  }

  public allocateSelected(trade: Trade) {
    this.onAllocateNextClick.emit(trade);
  }

  public isCurrencyColumn(element: TableColumn): boolean {
    return element.type === TableColumnType.currency;
  }

  public toggleRow(element: any): void {
    element.expanded = !element.expanded;
  }

  public isAccountFullAllocated(orders: TradeSummary[]): boolean{
      return _.sumBy(orders, (order) => order.quantity - order.quantityAllocated) === 0;
  }

}
